import React from "react";
import { Bell, Lock, User, Globe, Moon, CreditCard } from "lucide-react";
import { Button } from "../components/ui/Button";
import { useAppStore } from "../store";
import { usePlan } from "../hooks/usePlan";
import { openPortal } from "../lib/billing/stripeClient";

const settingsSections = [
  {
    id: "profile",
    title: "Profile Settings",
    icon: User,
    description: "Update your personal information and profile settings",
  },
  {
    id: "notifications",
    title: "Notifications",
    icon: Bell,
    description: "Configure how you receive notifications and alerts",
  },
  {
    id: "security",
    title: "Security",
    icon: Lock,
    description: "Manage your account security and privacy settings",
  },
  {
    id: "preferences",
    title: "Preferences",
    icon: Globe,
    description: "Customize your workspace preferences and defaults",
  },
];

export const Settings = () => {
  const { isDarkMode, toggleDarkMode } = useAppStore();
  const { planId, stripeCustomerId, isPremium, isLoading } = usePlan();
  const [portalLoading, setPortalLoading] = React.useState(false);

  const handleManageBilling = async () => {
    if (!stripeCustomerId) return;
    try {
      setPortalLoading(true);
      await openPortal(stripeCustomerId, `${window.location.origin}/settings`);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to open billing portal");
      setPortalLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CreditCard className="h-6 w-6 text-gray-400" />
                <div className="ml-4">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                    Billing & Subscription
                  </h2>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Current plan: <span className="font-medium">{planId}</span>
                  </p>
                </div>
              </div>
              {stripeCustomerId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManageBilling}
                  isLoading={portalLoading || isLoading}
                >
                  Manage Billing
                </Button>
              )}
              {!stripeCustomerId && isPremium && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Upgrade via Pricing page
                </p>
              )}
            </div>
          </div>
        </div>

        {settingsSections.map((section) => (
          <div
            key={section.id}
            className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <section.icon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-4">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                    {section.title}
                  </h2>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {section.description}
                  </p>
                </div>
                <div className="ml-auto">
                  <Button variant="outline" size="sm">
                    Edit
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Moon className="h-6 w-6 text-gray-400" />
                <div className="ml-4">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                    Dark Mode
                  </h2>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Toggle between light and dark theme
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={toggleDarkMode}>
                {isDarkMode ? "Disable" : "Enable"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
